import { displayDigits } from '../utils/displayDigits';
import { IoFolderOutline, IoChevronDownOutline, IoAddOutline, IoCloseOutline, IoSearchOutline, IoTrashOutline, IoCheckmark } from 'react-icons/io5';
import React, { useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { AthleteItem } from '../services/api';
import '../styles/athlete-management.css';

type Folder = { id: string; name: string; members: number[] };
const FOLDERS_CHANGED = 'vo2-athlete-folders-changed';

export function AthleteFolders({ ownerId, athletes, children, accordion = false, searchQuery = '', showEmptyState = true, showHeading = true }: {
  ownerId?: number; athletes: AthleteItem[]; children: (athletes: AthleteItem[]) => React.ReactNode; accordion?: boolean; searchQuery?: string; showEmptyState?: boolean; showHeading?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const key = `vo2-athlete-folders-v1-${ownerId}`;
  const [folders, setFolders] = useState<Folder[]>([]);
  const [active, setActive] = useState('all');
  const accordionId = useId();
  useEffect(() => { if (accordion && searchQuery.trim()) setActive('all'); }, [accordion, searchQuery]);
  const [editId, setEditId] = useState('');
  const [name, setName] = useState('');
  const [members, setMembers] = useState<number[]>([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setFolders([]); setActive('all'); dialog.current?.close();
    if (ownerId == null) return;
    const read = () => {
      try {
        const data: unknown = JSON.parse(localStorage.getItem(key) || '[]');
        const next = Array.isArray(data) ? data.filter((f): f is Folder => !!f && typeof f.id === 'string' && typeof f.name === 'string' && Array.isArray(f.members) && f.members.every((id: unknown) => Number.isInteger(id))) : [];
        setFolders(next);
        setActive(id => id === 'all' || id === 'none' || next.some(f => f.id === id) ? id : 'all');
      } catch { setFolders([]); setActive('all'); }
    };
    read();
    const sync = (e: StorageEvent) => { if (e.key === key || e.key === null) read(); };
    const syncLocal = (e: Event) => { if ((e as CustomEvent<string>).detail === key) read(); };
    window.addEventListener('storage', sync);
    window.addEventListener(FOLDERS_CHANGED, syncLocal);
    return () => { window.removeEventListener('storage', sync); window.removeEventListener(FOLDERS_CHANGED, syncLocal); };
  }, [key, ownerId]);
  const save = (next: Folder[]) => {
    try {
      localStorage.setItem(key, JSON.stringify(next)); setFolders(next); setError('');
      window.dispatchEvent(new CustomEvent(FOLDERS_CHANGED, { detail: key }));
      return true;
    } catch { setError(t('athleteFolders.saveError')); return false; }
  };
  const edit = (id: string) => {
    const folder = folders.find(f => f.id === id);
    setEditId(folder?.id || ''); setName(folder?.name || ''); setMembers(folder?.members || []); setQuery(''); setError(''); setConfirmDelete(false);
  };
  const visible = active === 'all' ? athletes : active === 'none'
    ? athletes.filter(a => !folders.some(f => f.members.includes(a.id)))
    : athletes.filter(a => folders.find(f => f.id === active)?.members.includes(a.id));
  const matchingMembers = athletes.filter(a => a.full_name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const memberCount = athletes.filter(a => members.includes(a.id)).length;
  const startNew = () => { edit(''); nameInput.current?.focus(); };

  const groups = [
    { id: 'all', name: t('athleteFolders.all'), members: athletes },
    { id: 'none', name: t('athleteFolders.ungrouped'), members: athletes.filter(a => !folders.some(f => f.members.includes(a.id))) },
    ...folders.map(folder => ({ id: folder.id, name: folder.name, members: athletes.filter(a => folder.members.includes(a.id)) })),
  ];
  return <>
    {!accordion && ownerId != null && <div className="athlete-folder-filter">
      {showHeading && <div className="athlete-folder-filter__heading"><span>{t('athleteFolders.filter')}</span>
        <button type="button" onClick={() => { edit(active === 'all' || active === 'none' ? folders[0]?.id || '' : active); dialog.current?.showModal(); }} aria-haspopup="dialog">{t('athleteManagement.organize')}</button>
      </div>}
      <label className="athlete-folder-filter__select"><IoFolderOutline size={16} aria-hidden="true" />
        <select aria-label={t('athleteFolders.filter')} value={active} onChange={e => setActive(e.target.value)}>
          <option value="all">{t('athleteFolders.all')} ({displayDigits(athletes.length)})</option>
          <option value="none">{t('athleteFolders.ungrouped')} ({displayDigits(athletes.filter(a => !folders.some(f => f.members.includes(a.id))).length)})</option>
          {folders.map(f => <option key={f.id} value={f.id}>{f.name} ({displayDigits(athletes.filter(a => f.members.includes(a.id)).length)})</option>)}
        </select>
      </label>
    </div>}
    {!accordion && showEmptyState && visible.length === 0 && athletes.length > 0 && <p className="athlete-empty">{t('athleteFolders.empty')}</p>}
    {!accordion && children(visible)}
    {accordion && <div className="athlete-group-accordion">
      {groups.map((group, index) => <section key={group.id} className="athlete-group-accordion__group">
        <button type="button" className="athlete-group-accordion__trigger" aria-expanded={active === group.id}
          aria-controls={`${accordionId}-${index}`} onClick={() => setActive(id => id === group.id ? '' : group.id)}>
          <IoFolderOutline size={18} aria-hidden="true" />
          <span>{group.name}</span><b>{displayDigits(group.members.length)}</b>
          <IoChevronDownOutline className="athlete-group-accordion__chevron" size={16} aria-hidden="true" />
        </button>
        <div id={`${accordionId}-${index}`} hidden={active !== group.id} className="athlete-group-accordion__body">
          {children(active === group.id ? group.members : [])}
          {group.members.length === 0 && <p className="athlete-empty">{t('athleteFolders.empty')}</p>}
        </div>
      </section>)}
    </div>}
    {createPortal(<dialog ref={dialog} className="athlete-folder-dialog" dir={i18n.dir()} aria-label={t('athleteFolders.manage')} onClick={e => { if (e.target === e.currentTarget) dialog.current?.close(); }}>
      <header className="athlete-folder-dialog__header">
        <div><h2>{t('athleteManagement.folderTitle')}</h2><p>{t('athleteManagement.folderSubtitle')}</p></div>
        <button type="button" className="athlete-icon-button" aria-label={t('athleteManagement.close')} onClick={() => dialog.current?.close()}><IoCloseOutline size={22} /></button>
      </header>
      <div className="athlete-folder-dialog__workspace">
        <nav className="athlete-folder-nav" aria-label={t('athleteFolders.manage')}>
          <button type="button" className="athlete-button athlete-button--new" onClick={startNew}><IoAddOutline size={18} />{t('athleteFolders.new')}</button>
          <div className="athlete-folder-nav__list">
            {folders.map(f => <button key={f.id} type="button" className={editId === f.id ? 'is-selected' : ''} aria-pressed={editId === f.id} onClick={() => edit(f.id)}>
              <IoFolderOutline size={18} /><span className="athlete-name" title={f.name}>{f.name}</span><span className="athlete-count">{athletes.filter(a => f.members.includes(a.id)).length}</span>
            </button>)}
            {!folders.length && <p className="athlete-empty">{t('athleteManagement.noFolders')}</p>}
          </div>
          <p className="athlete-folder-nav__note">{t('athleteFolders.local')}</p>
        </nav>
        <form className="athlete-folder-editor" onSubmit={e => {
          e.preventDefault(); const clean = name.trim();
          if (!clean) { nameInput.current?.focus(); return; }
          if (folders.some(f => f.id !== editId && f.name.toLocaleLowerCase() === clean.toLocaleLowerCase())) { setError(t('athleteFolders.duplicate')); return; }
          const folder = { id: editId || crypto.randomUUID(), name: clean, members };
          const next = editId ? folders.map(f => f.id === editId ? folder : f) : [...folders, folder];
          if (save(next)) { setActive(folder.id); dialog.current?.close(); }
        }}>
          <h3>{t(editId ? 'athleteFolders.edit' : 'athleteFolders.new')}</h3>
          <label className="athlete-folder-editor__field">{t('athleteFolders.name')}<input ref={nameInput} required maxLength={60} value={name} placeholder={t('athleteManagement.namePlaceholder')} onChange={e => { setName(e.target.value); setError(''); }} /></label>
          <div className="athlete-folder-editor__members-heading"><span>{t('app.myAthletes')}</span><span className="athlete-count">{t('athleteManagement.selectedCount', { count: memberCount })}</span></div>
          <label className="athlete-search"><IoSearchOutline size={17} aria-hidden="true" /><input type="search" value={query} onChange={e => setQuery(e.target.value)} aria-label={t('athleteFolders.search')} placeholder={t('athleteFolders.search')} /></label>
          <div className="athlete-folder-members">
            {matchingMembers.map(a => <label key={a.id} className={members.includes(a.id) ? 'is-selected' : ''}>
              <input type="checkbox" checked={members.includes(a.id)} onChange={e => setMembers(ids => e.target.checked ? [...ids, a.id] : ids.filter(id => id !== a.id))} />
              <span className="athlete-avatar athlete-avatar--initial" aria-hidden="true">{a.full_name.charAt(0).toUpperCase()}</span><span className="athlete-name">{a.full_name}</span>
            </label>)}
            {!matchingMembers.length && <p className="athlete-empty">{t(athletes.length ? 'athleteManagement.noResults' : 'sidebar.athletes.empty')}</p>}
          </div>
          {error && <p className="athlete-folder-error" role="alert">{error}</p>}
          {confirmDelete && <div className="athlete-folder-delete" role="alert"><p>{t('athleteManagement.confirmDelete', { name })}</p><p>{t('athleteFolders.deleteHint')}</p>
            <button type="button" className="athlete-button athlete-danger" onClick={() => { if (save(folders.filter(f => f.id !== editId))) { setActive('all'); edit(''); } }}>{t('athleteFolders.delete')}</button>
            <button type="button" className="athlete-button" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</button>
          </div>}
          <footer className="athlete-folder-editor__footer">
            {editId && <button type="button" className="athlete-icon-button athlete-danger" aria-label={t('athleteFolders.delete')} title={t('athleteFolders.delete')} onClick={() => setConfirmDelete(true)}><IoTrashOutline size={18} /></button>}
            <span className="athlete-folder-editor__spacer" />
            <button type="button" className="athlete-button" onClick={() => dialog.current?.close()}>{t('common.cancel')}</button>
            <button type="submit" className="athlete-button athlete-button--primary" disabled={!name.trim()}><IoCheckmark size={17} />{t('athleteFolders.save')}</button>
          </footer>
        </form>
      </div>
    </dialog>, document.body)}
  </>;
}
